import FingerprintJS from '@fingerprintjs/fingerprintjs';
import React, { useEffect, useState } from 'react';
import { MAIN_URL } from '../config';
import { setName } from '../store/slices/paymentSlice';
import { getItemFromLocalStorage } from '../utils/getItemFromLocalStorage';
import { useWindowSize } from '../hooks/useWindowSize';

interface RateUsProps {
  avgRating: number; // e.g., 4.5
}

const RateUs: React.FC<RateUsProps> = ({ avgRating }) => {
  const [fingerprint, setFingerprint] = useState<string>('');
  const [showDrawer, setShowDrawer] = useState<boolean>(false);
  const [fullName, setFullName] = useState<string>(''); // For capturing full name
  const [proofFile, setProofFile] = useState<File | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);
  const MENU_CONFIG=getItemFromLocalStorage("menuConfiguration");
  const { width } = useWindowSize();
  const isMobile = width < 640;

  useEffect(() => {
    FingerprintJS.load()
      .then(fp => fp.get())
      .then(result => {
        const visitorId = result.visitorId;
        setFingerprint(visitorId);
      })
      .catch(error => console.error('Error generating fingerprint:', error));
  }, []);

  // Check local storage for submission flag on mount
  useEffect(() => {
    const submitted = localStorage.getItem('reviewSubmitted');
    if (submitted === 'true') {
      setHasSubmitted(true);
    }
  }, []);

  // Clear success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleRate = () => {
    // Always open the popup drawer to submit proof
    
    // Replace with your coordinates or location
    window.open(MENU_CONFIG.locationLink, "_blank");

    if(MENU_CONFIG.reviewReward===true)
    {
      setShowDrawer(true);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const maxSize = 10 * 1024 * 1024; // 10 MB in bytes
      // Define allowed file types using a regex based on the file extension.
      const allowedFileTypes = /(\.jpg|\.jpeg|\.png|\.heic|\.heif)$/i;
  
      if (!allowedFileTypes.test(file.name)) {
        setErrorMessage('Unsupported file type. Please upload an image in jpeg, jpg, png, svg, gif, heic, or heif format.');
        return;
      }
  
      if (file.size > maxSize) {
        setErrorMessage('File size must not exceed 10 MB.');
        return;
      }
      
      setErrorMessage('');
      setProofFile(file);
    }
  };
  

  const handleDrawerSubmit = async () => {
    // Validate mandatory fields
    if (!fullName.trim() || !proofFile) {
      setErrorMessage('Both your full name and a screenshot of your review are required.');
      return;
    }

    // Clear error if validation passes
    setErrorMessage('');
    setIsLoading(true);

    // Prepare a FormData object to include the file and full name data
    const formData = new FormData();
    formData.append('fingerprint', fingerprint);
    formData.append('fullName', fullName);
    formData.append('proofFile', proofFile);

    try {
      const response = await fetch(MAIN_URL + 'rating', {
        method: 'POST',
        body: formData,
      });
      if (response.ok) {
        // setSuccessMessage('Your review has been submitted successfully.');
        setSuccessMessage("Thank you for submitting your review! Reach out to our Restaurant Captain or Manager to assist with your offer redemption.");

        setShowDrawer(false);
        setProofFile(null);
        setName("");
        setHasSubmitted(true);
        // Save submission state permanently in local storage
        localStorage.setItem('reviewSubmitted', 'true');
      } else {
        const data = await response.json();
        setErrorMessage(`${data.message}`);
        setProofFile(null);
        setName("");
      }
    } catch (error) {
      setErrorMessage('There was an error submitting your review proof.');
    } finally {
      setIsLoading(false);
    }
  };

  const styles = {
    container: {
      backgroundColor: '#ffffff',
      borderRadius: 20,
      padding: 20,
      margin: '16px 0',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
      border: '1px solid rgba(0, 0, 0, 0.06)',
      position: 'relative' as const,
      overflow: 'hidden' as const,
    },
    decoration: {
      position: 'absolute' as const,
      top: -20,
      right: -20,
      width: 100,
      height: 100,
      background: 'linear-gradient(135deg, rgba(0, 176, 185, 0.1) 0%, rgba(138, 43, 226, 0.1) 100%)',
      borderRadius: '50%',
      filter: 'blur(30px)',
    },
    mobileLayout: {
      display: isMobile ? 'flex' : 'none',
      flexDirection: 'column' as const,
      position: 'relative' as const,
      zIndex: 2,
    },
    desktopLayout: {
      display: isMobile ? 'none' : 'flex',
      alignItems: 'center' as const,
      justifyContent: 'space-between' as const,
      position: 'relative' as const,
      zIndex: 2,
    },
    headerRow: {
      display: 'flex',
      alignItems: 'flex-start' as const,
      justifyContent: 'space-between' as const,
      marginBottom: 12,
    },
    titleSection: {
      flex: 1,
    },
    titleRow: {
      display: 'flex',
      alignItems: 'center' as const,
      gap: 8,
      marginBottom: 4,
    },
    indicator: {
      width: 8,
      height: 8,
      borderRadius: '50%',
      background: 'linear-gradient(135deg, var(--main-turquoise) 0%, #8a2be2 100%)',
    },
    title: {
      fontFamily: 'DM Sans, sans-serif',
      fontSize: 16,
      fontWeight: 700,
      color: 'var(--main-color)',
      margin: 0,
    },
    ratingBadge: {
      display: 'flex',
      alignItems: 'center' as const,
      backgroundColor: '#fff8e1',
      padding: '8px 12px',
      borderRadius: 12,
      border: '1px solid rgba(255, 193, 7, 0.3)',
      boxShadow: '0 2px 8px rgba(255, 193, 7, 0.15)',
      marginLeft: 12,
    },
    ratingValue: {
      fontFamily: 'DM Sans, sans-serif',
      fontSize: 18,
      fontWeight: 700,
      color: 'var(--main-color)',
      marginRight: 4,
    },
    starIcon: {
      width: 18,
      height: 18,
      color: '#ffc107',
    },
    description: {
      fontFamily: 'DM Sans, sans-serif',
      fontSize: 14,
      lineHeight: 1.6,
      color: '#666',
      fontWeight: 500,
      marginBottom: 20,
    },
    googleText: {
      display: 'inline-flex',
      alignItems: 'center' as const,
      gap: 4,
      fontWeight: 600,
      color: '#4285F4',
    },
    googleIcon: {
      width: 16,
      height: 16,
    },
    rewardText: {
      color: '#10b981',
      fontWeight: 600,
    },
    rewardHighlight: {
      background: 'linear-gradient(135deg, #10b981 0%, #14b8a6 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
    },
    buttonContainer: {
      display: 'flex',
      justifyContent: 'center' as const,
    },
    rateButton: {
      position: 'relative' as const,
      display: 'inline-flex',
      alignItems: 'center' as const,
      justifyContent: 'center' as const,
      padding: '12px 28px',
      backgroundColor: 'var(--main-turquoise)',
      color: '#ffffff',
      borderRadius: 30,
      border: 'none',
      fontFamily: 'DM Sans, sans-serif',
      fontSize: 14,
      fontWeight: 600,
      cursor: 'pointer',
      boxShadow: '0 4px 16px rgba(0, 176, 185, 0.3)',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      overflow: 'hidden' as const,
      gap: 8,
    },
    rateButtonHover: {
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 24px rgba(0, 176, 185, 0.4)',
    },
    buttonGlow: {
      position: 'absolute' as const,
      inset: 0,
      background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, transparent 100%)',
      borderRadius: 30,
      opacity: 0,
      transition: 'opacity 0.3s ease',
    },
    buttonText: {
      position: 'relative' as const,
      zIndex: 2,
    },
    arrowIcon: {
      width: 16,
      height: 16,
      position: 'relative' as const,
      zIndex: 2,
      transition: 'transform 0.2s ease',
    },
    submittedBadge: {
      display: 'flex',
      alignItems: 'center' as const,
      gap: 8,
      padding: '10px 20px',
      backgroundColor: '#ecfdf5',
      border: '1px solid rgba(16, 185, 129, 0.3)',
      borderRadius: 30,
    },
    submittedDot: {
      width: 8,
      height: 8,
      borderRadius: '50%',
      backgroundColor: '#10b981',
      animation: 'pulse 2s infinite',
    },
    submittedText: {
      fontFamily: 'DM Sans, sans-serif',
      fontSize: 14,
      fontWeight: 600,
      color: '#059669',
    },
    desktopTextSection: {
      flex: 1,
      paddingRight: 20,
    },
    desktopButtonSection: {
      display: 'flex',
      alignItems: 'center' as const,
      gap: 16,
    },
    desktopRateButton: {
      width: 48,
      height: 48,
      borderRadius: '50%',
      backgroundColor: 'var(--main-turquoise)',
      border: 'none',
      display: 'flex',
      alignItems: 'center' as const,
      justifyContent: 'center' as const,
      cursor: 'pointer',
      boxShadow: '0 4px 16px rgba(0, 176, 185, 0.3)',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      position: 'relative' as const,
      overflow: 'hidden' as const,
    },
    desktopArrowIcon: {
      width: 20,
      height: 20,
      color: '#ffffff',
      position: 'relative' as const,
      zIndex: 2,
    },
  };

  return (
    <div style={styles.container}>
      {/* Background decoration */}
      <div style={styles.decoration}></div>
      
      {/* Mobile Layout */}
      <div style={styles.mobileLayout}>
        {/* Top Section: Title and Rating */}
        <div style={styles.headerRow}>
          <div style={styles.titleSection}>
            <div style={styles.titleRow}>
              <div style={styles.indicator}></div>
              <h3 style={styles.title}>Delighted with Our Service?</h3>
            </div>
          </div>
          {/* Rating Display - Mobile */}
          <div style={styles.ratingBadge}>
            <span style={styles.ratingValue}>{avgRating}</span>
            <svg style={styles.starIcon} fill="currentColor" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.954a1 1 0 00.95.69h4.162c.969 0 1.371 1.24.588 1.81l-3.37 2.448a1 1 0 00-.364 1.118l1.287 3.955c.3.92-.755 1.688-1.54 1.118l-3.37-2.448a1 1 0 00-1.175 0l-3.37 2.448c-.784.57-1.838-.197-1.539-1.118l1.287-3.955a1 1 0 00-.364-1.118L2.07 9.38c-.783-.57-.38-1.81.588-1.81h4.162a1 1 0 00.95-.69l1.286-3.954z" />
            </svg>
          </div>
        </div>

        {/* Description Text - Mobile */}
        <p style={styles.description}>
          Share your experience on{' '}
          <span style={styles.googleText}>
            <svg style={styles.googleIcon} viewBox="0 0 24 24" fill="currentColor">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Google
          </span>
          {MENU_CONFIG.reviewReward === true && (
            <span style={styles.rewardText}>
              {' '}and enjoy a <span style={styles.rewardHighlight}>{MENU_CONFIG.rewardDescription}</span>
            </span>
          )}
          .
        </p>

        {/* Action Button - Mobile */}
        <div style={styles.buttonContainer}>
          {!hasSubmitted ? (
            <button
              onClick={handleRate}
              style={styles.rateButton}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 24px rgba(0, 176, 185, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 176, 185, 0.3)';
              }}
            >
              <span style={styles.buttonText}>Rate on Google</span>
              <svg style={styles.arrowIcon} fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </button>
          ) : (
            <div style={styles.submittedBadge}>
              <div style={styles.submittedDot}></div>
              <span style={styles.submittedText}>Review Submitted</span>
            </div>
          )}
        </div>
      </div>

      {/* Desktop Layout */}
      <div style={styles.desktopLayout}>
        {/* Left Section: Text */}
        <div style={styles.desktopTextSection}>
          <div style={styles.titleRow}>
            <div style={styles.indicator}></div>
            <h3 style={{...styles.title, fontSize: 18}}>Delighted with Our Service?</h3>
          </div>
          <p style={{...styles.description, marginBottom: 0}}>
            Share your experience on{' '}
            <span style={styles.googleText}>
              <svg style={{...styles.googleIcon, width: 18, height: 18}} viewBox="0 0 24 24" fill="currentColor">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              Google
            </span>
            {MENU_CONFIG.reviewReward === true && (
              <span style={styles.rewardText}>
                {' '}and enjoy a <span style={styles.rewardHighlight}>{MENU_CONFIG.rewardDescription}</span>
              </span>
            )}
            .
          </p>
        </div>

        {/* Right Section: Rating & Button */}
        <div style={styles.desktopButtonSection}>
          {/* Rating Display */}
          <div style={{...styles.ratingBadge, padding: '10px 14px'}}>
            <span style={{...styles.ratingValue, fontSize: 20}}>{avgRating}</span>
            <svg style={{...styles.starIcon, width: 20, height: 20}} fill="currentColor" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.954a1 1 0 00.95.69h4.162c.969 0 1.371 1.24.588 1.81l-3.37 2.448a1 1 0 00-.364 1.118l1.287 3.955c.3.92-.755 1.688-1.54 1.118l-3.37-2.448a1 1 0 00-1.175 0l-3.37 2.448c-.784.57-1.838-.197-1.539-1.118l1.287-3.955a1 1 0 00-.364-1.118L2.07 9.38c-.783-.57-.38-1.81.588-1.81h4.162a1 1 0 00.95-.69l1.286-3.954z" />
            </svg>
          </div>
          
          {/* Rate Button */}
          {!hasSubmitted ? (
            <button
              onClick={handleRate}
              style={styles.desktopRateButton}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px) scale(1.05)';
                e.currentTarget.style.boxShadow = '0 6px 24px rgba(0, 176, 185, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0) scale(1)';
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 176, 185, 0.3)';
              }}
            >
              <svg style={styles.desktopArrowIcon} fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </button>
          ) : (
            <div style={styles.submittedBadge}>
              <div style={styles.submittedDot}></div>
              <span style={styles.submittedText}>Submitted</span>
            </div>
          )}
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="mt-4 p-4 bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200/60 rounded-xl relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-emerald-400 to-green-400"></div>
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center mt-0.5">
              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </div>
            <p className="text-emerald-700 text-sm font-medium leading-relaxed">{successMessage}</p>
          </div>
        </div>
      )}

      {/* Modern Popup Drawer */}
      {showDrawer && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4">
          {/* Enhanced Backdrop */}
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300" 
            onClick={() => setShowDrawer(false)}
          ></div>
          
          {/* Modern Drawer */}
          <div className="bg-white rounded-2xl sm:rounded-3xl p-6 z-50 w-full sm:max-w-lg shadow-2xl transform transition-all duration-300 border border-gray-200/60 relative overflow-hidden">
            {/* Header decoration */}
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 to-purple-500"></div>
            
            {/* Close button */}
            <button
              onClick={() => setShowDrawer(false)}
              className="absolute top-4 right-4 w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors duration-200 group"
            >
              <svg className="w-4 h-4 text-gray-600 group-hover:text-gray-800" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Content */}
            <div className="pr-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-800">Review Verification</h3>
              </div>
              
              <p className="text-gray-600 text-sm leading-relaxed mb-6">
                To verify your review submission, please provide your full name and upload a screenshot of your review.
              </p>

              {/* File Upload */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Screenshot Upload</label>
                <div className="relative">
                  <input 
                    type="file" 
                    accept="image/*" 
                    onChange={handleFileChange} 
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                  />
                  <div className="border-2 border-dashed border-gray-300 hover:border-blue-400 rounded-xl p-6 text-center transition-colors duration-200 bg-gray-50/50 hover:bg-blue-50/50">
                    <div className="flex flex-col items-center gap-2">
                      <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 16.5V9.75m0 0l3 3m-3-3l-3 3M6.75 19.5a4.5 4.5 0 01-1.41-8.775 5.25 5.25 0 0110.233-2.33 3 3 0 013.758 3.848A3.752 3.752 0 0118 19.5H6.75z" />
                      </svg>
                      <span className="text-sm font-medium text-gray-600">
                        {proofFile ? proofFile.name : "Click to upload or drag and drop"}
                      </span>
                      <span className="text-xs text-gray-500">PNG, JPG, HEIC up to 10MB</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Name Input */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                <input
                  type="text"
                  value={fullName}
                  maxLength={100}
                  onChange={(e) => setFullName(e.target.value)}
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors duration-200"
                  placeholder="Enter your full name"
                />
              </div>

              {/* Error Message */}
              {errorMessage && (
                <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-xl">
                  <p className="text-red-600 text-sm flex items-center gap-2">
                    <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    {errorMessage}
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button 
                  onClick={() => setShowDrawer(false)} 
                  className="flex-1 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-xl transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDrawerSubmit}
                  disabled={isLoading}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-medium rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                      Submit
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RateUs;
